import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Sparkles, FolderPlus } from "lucide-react";

export default function CreateResearchModal({ isOpen, onClose, onCreate }) {
    const [researchName, setResearchName] = useState("");

    const handleSubmit = (e) => {
        e.preventDefault();
        if (researchName.trim()) {
            onCreate(researchName);
            setResearchName("");
            onClose();
        }
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        className="fixed inset-0 bg-[#020617]/80 z-[60] backdrop-blur-md"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                    />

                    {/* Modal Container */}
                    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 pointer-events-none">
                        <motion.div
                            className="bg-[#0f172a] rounded-[2.5rem] border border-white/10 shadow-[0_0_50px_-12px_rgba(79,70,229,0.3)] w-full max-w-md overflow-hidden pointer-events-auto relative"
                            initial={{ opacity: 0, scale: 0.9, y: 20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.9, y: 20 }}
                        >
                            {/* Decorative Background */}
                            <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-600/10 blur-3xl -z-10"></div>

                            <div className="p-8">
                                <div className="flex justify-between items-center mb-8">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 bg-indigo-500/10 rounded-xl flex items-center justify-center text-indigo-400 border border-indigo-500/20">
                                            <FolderPlus size={20} />
                                        </div>
                                        <h3 className="text-xl font-bold text-white tracking-tight">
                                            New Research
                                        </h3>
                                    </div>
                                    <button
                                        onClick={onClose}
                                        className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center text-white/40 hover:text-white hover:bg-white/10 transition-all"
                                    >
                                        <X size={18} />
                                    </button>
                                </div>

                                <form onSubmit={handleSubmit} className="space-y-8">
                                    <div className="space-y-3">
                                        <label
                                            htmlFor="researchName"
                                            className="block text-xs font-bold uppercase tracking-widest text-white/30 ml-1"
                                        >
                                            Project Name
                                        </label>
                                        <div className="relative group">
                                            <input
                                                type="text"
                                                id="researchName"
                                                value={researchName}
                                                onChange={(e) => setResearchName(e.target.value)}
                                                placeholder="e.g. Molecular Biology Review"
                                                className="w-full bg-white/5 border border-white/10 rounded-2xl px-5 py-4 text-white placeholder:text-white/20 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 transition-all text-sm"
                                                autoFocus
                                            />
                                            <div className="absolute right-4 top-1/2 -translate-y-1/2 text-indigo-400 opacity-0 group-focus-within:opacity-100 transition-opacity">
                                                <Sparkles size={16} />
                                            </div>
                                        </div>
                                        <p className="text-[10px] text-white/20 ml-1">
                                            This will create a dedicated container for your academic papers.
                                        </p>
                                    </div>

                                    <div className="flex gap-3">
                                        <button
                                            type="button"
                                            onClick={onClose}
                                            className="flex-1 px-5 py-4 rounded-2xl bg-white/5 text-white/60 font-bold text-sm hover:bg-white/10 transition-all"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            disabled={!researchName.trim()}
                                            className="flex-[2] px-5 py-4 rounded-2xl bg-gradient-to-r from-indigo-600 to-indigo-700 text-white font-bold text-sm hover:from-indigo-500 hover:to-indigo-600 disabled:opacity-30 disabled:cursor-not-allowed transition-all shadow-xl shadow-indigo-600/20 active:scale-95"
                                        >
                                            Initialize Project
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </motion.div>
                    </div>
                </>
            )}
        </AnimatePresence>
    );
}
